import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { addDays, endOfMonth, format, startOfDay, startOfMonth, startOfWeek } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { SavedPicks } from "@/components/SavedPicks";

const ymd = (d) => format(d, "yyyy-MM-dd");

// Selection -> list of [startDay, lastDay] (inclusive, local days)
function spans(v) {
  const t = new Date();
  if (v.kind === "today") return [[t, t]];
  if (v.kind === "week") { const s = startOfWeek(t); return [[s, addDays(s, 6)]]; }
  if (v.kind === "month") return [[startOfMonth(t), endOfMonth(t)]];
  if (v.kind === "days") return v.days.map((d) => [d, d]);
  if (v.kind === "range" && v.range?.from) return [[v.range.from, v.range.to || v.range.from]];
  return [];
}

export function dateParams(v, basis) {
  const sp = spans(v);
  if (!sp.length) return {};
  if (basis === "visit") return { visit: sp.map(([a, b]) => `${ymd(a)}~${ymd(b)}`).join(",") };
  return { created: sp.map(([a, b]) => `${startOfDay(a).toISOString()}~${addDays(startOfDay(b), 1).toISOString()}`).join(",") };
}

export function dateLabel(v) {
  if (v.kind === "days") return v.days.length === 1 ? format(v.days[0], "MMM d") : `${v.days.length} days`;
  if (v.kind === "range" && v.range?.from) return `${format(v.range.from, "MMM d")} - ${format(v.range.to || v.range.from, "MMM d")}`;
  return "Calendar";
}

const QUICK = [["all", "All"], ["today", "Today"], ["week", "Week"], ["month", "Month"]];

function useDayCounts(month, open, basis, params) {
  const [counts, setCounts] = useState({});
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!open) { return; }
    const s = startOfWeek(startOfMonth(month));
    const req = basis === "visit"
      ? api.get("/packets/visit-day-counts", { params: { ...JSON.parse(key), start: ymd(s), end: ymd(addDays(s, 41)) } })
      : api.get("/packets/day-counts", { params: { ...JSON.parse(key), start: s.toISOString(), end: addDays(s, 42).toISOString(), tz_offset: new Date().getTimezoneOffset() } });
    req.then((r) => setCounts(r.data)).catch(() => setCounts({}));
  }, [month, open, basis, key]);
  return counts;
}

function Toggle({ items, value, onPick, prefix }) {
  return (
    <div className="flex gap-1 p-1 bg-slate-100 rounded-md">
      {items.map(([k, l]) => (
        <button key={k} onClick={() => k !== value && onPick(k)} data-testid={`${prefix}-${k}`}
          className={`flex-1 text-xs h-7 rounded transition-colors ${value === k ? "bg-white shadow-sm font-bold" : "text-slate-600"}`}>{l}</button>))}
    </div>
  );
}

function CalendarPicker({ value, onChange, basis, setBasis, countParams }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(value.kind === "range" ? "range" : "days");
  const [month, setMonth] = useState(new Date());
  const counts = useDayCounts(month, open, basis, countParams);
  useEffect(() => { if (value.kind === "range" || value.kind === "days") { setMode(value.kind); } }, [value.kind]);
  const DayContent = ({ date }) => {
    const n = counts[ymd(date)];
    return (<span className="w-full h-full flex flex-col items-center justify-center leading-none gap-0.5">{date.getDate()}
      {n > 0 && <span className="text-[9px] font-bold text-[var(--brand)] [[aria-selected=true]_&]:text-white" data-testid={`day-count-${ymd(date)}`}>{n}</span>}</span>);
  };
  const pick = (sel) => {
    if (mode === "days") onChange(sel?.length ? { kind: "days", days: [...sel].sort((a, b) => a - b) } : { kind: "all" });
    else onChange(sel?.from ? { kind: "range", range: sel } : { kind: "all" });
  };
  const active = value.kind === "days" || value.kind === "range";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant={active ? "default" : "outline"} className="h-8" data-testid="date-filter-calendar-btn">
          <CalendarDays className="w-3.5 h-3.5 mr-1" />{dateLabel(value)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2 space-y-1" align="start" data-testid="date-filter-popover">
        <Toggle prefix="date-basis" value={basis} onPick={setBasis} items={[["visit", "Visit / due date"], ["created", "Packet created"]]} />
        <Toggle prefix="date-mode" value={mode} onPick={(k) => { setMode(k); onChange({ kind: "all" }); }} items={[["days", "Tap days"], ["range", "Start - end"]]} />
        <Calendar mode={mode === "days" ? "multiple" : "range"} month={month} onMonthChange={setMonth}
          selected={mode === "days" ? (value.kind === "days" ? value.days : []) : (value.kind === "range" ? value.range : undefined)}
          onSelect={pick} components={{ DayContent }} classNames={{ day: "h-10 w-10 p-0 font-normal rounded-md hover:bg-slate-100 aria-selected:opacity-100", day_range_middle: "aria-selected:!bg-slate-200 aria-selected:!text-slate-900", head_cell: "text-muted-foreground w-10 font-normal text-[0.8rem]" }} />
        <div className="flex items-center justify-between gap-3 px-2 text-[11px] text-slate-500">
          <span data-testid="date-count-legend">Numbers = {basis === "visit" ? "packets with that visit / due date" : "packets created that day"}</span>
          <button className="underline" onClick={() => onChange({ kind: "all" })} data-testid="date-filter-clear">Clear</button>
        </div>
        <SavedPicks value={value} basis={basis} onApply={(v, b) => { setBasis(b); onChange(v); if (v.kind === "range" || v.kind === "days") { setMode(v.kind); setMonth(v.kind === "range" ? v.range.from : v.days[0]); } }} />
      </PopoverContent>
    </Popover>
  );
}

export function DateFilter({ value, onChange, basis, setBasis, countParams }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto whitespace-nowrap" data-testid="date-filter">
      {QUICK.map(([k, l]) => (
        <Button key={k} size="sm" variant={value.kind === k ? "default" : "outline"} className="h-8 px-3" onClick={() => onChange({ kind: k })} data-testid={`date-filter-${k}`}>{l}</Button>
      ))}
      <CalendarPicker value={value} onChange={onChange} basis={basis} setBasis={setBasis} countParams={countParams} />
    </div>
  );
}
